import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Form, Select, Spin, Input, Alert, InputNumber, Tooltip } from "antd";
import dayjs from "dayjs";
import { Package, Save, Edit, Zap, Plus, Trash2, Calculator } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useClientStore } from "~/components/admin/client-management/clients/clients.store";
import { FormProject } from "~/components/admin/form-project";
import { InfiniteScrollSelect } from "~/components/common";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { useInventoryStore } from "../../../inventories/inventory.store";
import {
  BOOKING_KEYS,
  useCreateBooking,
  useUpdateBooking,
} from "~/services/booking";
import { useCreateEstimate } from "~/services/estimate";
import { useGetClients } from "~/services/client/client.queries";
import { useGetProjectsInfinite } from "~/services/project";
import { useGetActiveInventoryTemplates } from "~/services/inventory-templates/inventory-templates.queries";
import { useGetPackingMetrics } from "~/services/service-metrics";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { useGetStaffRoleByName } from "~/services/common";
import { FieldType, useBooking } from "~/store";
import { TBooking } from "~/types/booking";
import { ClientStatus, JobStatus, ServiceType, PackingTypes } from "~/utils/constants";
import { useNavigate } from "react-router-dom";

type Props = {
  openAddService: () => void;
  booking?: TBooking;
};

export const BookingInfoForm: FC<Props> = ({ booking }) => {
  const { openClientModal } = useClientStore();
  const inventoryStore = useInventoryStore();
  const [openCreateProject, setOpenCreateProjectModal] = useState(false);
  const navigate = useNavigate();

  const qc = useQueryClient();
  const { success, error } = useNotification();

  const {
    filters,
    viewMode,
    clientId,
    bookingId,
    closeBookingModal,
    setInfoForm,
    isEstimateMode,
  } = useBooking();

  const [form] = Form.useForm<FieldType>();
  const selectedClientId = Form.useWatch("clientId", form);
  const clientNameValue = Form.useWatch("clientName", form);
  const [preservedClientId, setPreservedClientId] = useState<string | null>(null);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  
  // Estimate mode state
  const [selectedTemplates, setSelectedTemplates] = useState<Array<{
    templateId: string;
    templateName: string;
    quantity: number;
    packingType: PackingTypes | null;
    wePack: boolean;
  }>>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType>(ServiceType.COLLECTION);
  const [selectedVesselId, setSelectedVesselId] = useState<string | undefined>(undefined);
  const [travelTime, setTravelTime] = useState<number>(0.5); // Default 30 min
  const [staffCount, setStaffCount] = useState<number>(2); // Default 2 staff
  const [duration, setDuration] = useState<number>(1); // Default 1 hour
  const [estimateNotes, setEstimateNotes] = useState<string>(""); // Notes/scope for estimate
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [priceBreakdown, setPriceBreakdown] = useState<{
    packingMaterials: number;
    packingDuration: number; // in minutes
    serviceCost: number;
    suppliesCost: number;
    total: number;
  } | null>(null);
  
  // Fetch data for estimate features
  const { data: inventoryTemplates } = useGetActiveInventoryTemplates();
  const { data: packingMetrics } = useGetPackingMetrics();
  const { data: vessels } = useGetVesselsForDropdown();
  const { data: artHandlerRole } = useGetStaffRoleByName("Art Handler", selectedClientId || undefined);

  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  const infiniteProjects = useGetProjectsInfinite();

  const mutation = useCreateBooking();
  const mutationUpdate = useUpdateBooking();
  const createEstimateMutation = useCreateEstimate();

  useEffect(() => {
    if (!clientId || bookingId) return;
    form.setFieldsValue({
      clientId: clientId,
    });
  }, [bookingId, clientId, form]);

  useEffect(() => {
    if (booking) {
      form.setFieldsValue({
        clientId: booking.clientId,
        projectId: booking.projectId,
      });
      setPreservedClientId(booking.clientId);
    }
  }, [form, booking]);

  // Restore client selection after client data refresh
  useEffect(() => {
    if (preservedClientId && clients?.items?.length && !selectedClientId) {
      // Only restore if the client still exists and no client is currently selected
      const clientExists = clients.items.some(client => client.id === preservedClientId);
      if (clientExists) {
                if (preservedClientId) {
                  form.setFieldValue('clientId', preservedClientId);
                }
      }
    }
  }, [clients, form, preservedClientId, selectedClientId]);

  const isEditMode = useMemo(() => Boolean(booking), [booking]);
  const viewOnly = useMemo(() => viewMode === "view", [viewMode]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    console.log('=== FORM SUBMIT ===');
    console.log('Form values:', values);
    console.log('Is Estimate Mode:', isEstimateMode);

    const formValues = values as any;

    // Quick client takes precedence when form is shown and has data
    if (showNewClientForm && formValues.clientName) {
      if (!formValues.clientEmail && !formValues.clientPhone) {
        error({ message: "Please provide either email or phone number" });
        return;
      }

      mutation.mutate(
        {
          tempClientData: {
            name: formValues.clientName,
            email: formValues.clientEmail,
            phone: formValues.clientPhone,
          },
          status: JobStatus.DRAFT,
          bookingDate: dayjs().toISOString(),
        } as any,
        {
          onSuccess: (booking: TBooking) => {
            success({ message: "Booking created with quick client!" });
            closeBookingModal();
            navigate(`/bookings/${booking.id}`);
            qc.invalidateQueries({
              queryKey: BOOKING_KEYS.GET_BOOKINGS(filters),
            });
          },
          onError: (err) => {
            error({ message: err.message });
          },
        }
      );
      return;
    }

    if (bookingId) {
      const payload = {
        id: bookingId,
        ...values,
        bookingDate: dayjs().toISOString(),
      };

      mutationUpdate.mutate(payload, {
        onSuccess: () => {
          success({ message: "Booking updated successfully" });
          qc.invalidateQueries({
            queryKey: BOOKING_KEYS.GET_BOOKINGS(filters),
          });
          navigate(`/bookings/${bookingId}`);
          closeBookingModal();
        },
        onError: (err) => {
          error({ message: err.message });
        },
      });
    } else {
      mutation.mutate(
        {
          ...values,
          status: JobStatus.DRAFT,
          bookingDate: dayjs().toISOString(),
        },
        {
          onSuccess: (booking: TBooking) => {
            success({ message: "Booking created successfully" });
            closeBookingModal();
            navigate(`/bookings/${booking.id}`);
            qc.invalidateQueries({
              queryKey: BOOKING_KEYS.GET_BOOKINGS(filters),
            });
          },
          onError: (err) => {
            console.log("err", err);
            error({ message: err.message });
          },
        }
      );
    }
  };

  const handleAddInventory = () => {
    console.log("=== INVENTORY DEBUGGING ===");
    console.log("Selected client ID:", selectedClientId);
    
    // Set the client ID in formState first
    if (selectedClientId && inventoryStore.formState) {
      inventoryStore.formState.clientId = selectedClientId;
      inventoryStore.formState.initialClientId = selectedClientId;
      inventoryStore.formState.defaultClientId = selectedClientId;
    }
    
    // Open the modal
    inventoryStore.openInventoryModal();
    
    // Try to access the form instance after it renders
    if (selectedClientId) {
      setTimeout(() => {
        console.log("Attempting to set form values directly...");
        
        // Try to access any form instances on the page
        if (window.antdForms && window.antdForms.length > 0) {
          console.log("Found window.antdForms:", window.antdForms.length);
          const lastForm = window.antdForms[window.antdForms.length - 1];
          lastForm.setFieldsValue({ clientId: selectedClientId });
        }
        
        // Alternative: try to trigger the form's onChange
        const modalForms = document.querySelectorAll('.ant-modal .ant-form');
        console.log(`Found ${modalForms.length} modal forms`);
        
        if (modalForms.length > 0) {
          // Try to find the specific client field
          const clientField = modalForms[modalForms.length - 1].querySelector('[data-testid="clientId"], .ant-select');
          console.log("Client field found:", !!clientField);
          
          if (clientField) {
            // Try to trigger React's internal events
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value")?.set;
            if (nativeInputValueSetter) {
              nativeInputValueSetter.call(clientField, selectedClientId);
              
              const event = new Event('input', { bubbles: true });
              clientField.dispatchEvent(event);
            }
          }
        }
      }, 300);
    }
  };

  const handleEditClient = () => {
    if (selectedClientId) {
      // Preserve the current client ID before opening modal
      setPreservedClientId(selectedClientId);
      openClientModal(selectedClientId);
    }
  };

  // Template management functions
  const addTemplateRow = () => {
    setSelectedTemplates([...selectedTemplates, {
      templateId: '',
      templateName: '',
      quantity: 1,
      packingType: null,
      wePack: true,
    }]);
    setCalculatedPrice(null);
    setPriceBreakdown(null);
  };

  const removeTemplateRow = (index: number) => {
    setSelectedTemplates(selectedTemplates.filter((_, i) => i !== index));
    setCalculatedPrice(null); // Reset price when templates change
    setPriceBreakdown(null);
  };

  const updateTemplateRow = (index: number, field: string, value: any) => {
    setSelectedTemplates(prev => prev.map((t, i) => {
      if (i !== index) return t;
      if (field === 'templateId') {
        const template = inventoryTemplates?.find((tmpl: any) => tmpl.id === value);
        return { ...t, templateId: value, templateName: template?.name || '' };
      }
      return { ...t, [field]: value };
    }));
    setCalculatedPrice(null); // Reset price when templates change
    setPriceBreakdown(null);
  };

  // Get available packing types for a template
  const getAvailablePackingTypes = (templateId: string): string[] => {
    const template = inventoryTemplates?.find((t: any) => t.id === templateId);
    if (!template || !packingMetrics) return [];

    const length = template.length || 0;
    const width = template.width || 0;
    const height = template.height || 0;

    const fittingMetrics = packingMetrics.filter((metric) => {
      const maxL = Number(metric.maxLength) || Infinity;
      const maxW = Number(metric.maxWidth) || Infinity;
      const maxH = Number(metric.maxHeight) || Infinity;
      return length <= maxL && width <= maxW && height <= maxH;
    });

    return [...new Set(fittingMetrics.map(m => m.packingType))];
  };

  // Get the best metric for a specific packing type and template
  const getBestMetricForType = (templateId: string, packingType: string) => {
    const template = inventoryTemplates?.find((t: any) => t.id === templateId);
    if (!template || !packingMetrics) return null;

    const length = template.length || 0;
    const width = template.width || 0;
    const height = template.height || 0;

    // Filter to metrics of this type that fit, sorted by closest fit
    const fittingMetrics = packingMetrics
      .filter((metric) => {
        if (metric.packingType !== packingType) return false;
        const maxL = Number(metric.maxLength) || Infinity;
        const maxW = Number(metric.maxWidth) || Infinity;
        const maxH = Number(metric.maxHeight) || Infinity;
        return length <= maxL && width <= maxW && height <= maxH;
      })
      .sort((a, b) => {
        // Sort by closest fit (smallest max dimensions that still fit)
        const aVolume = (Number(a.maxLength) || 999) * (Number(a.maxWidth) || 999) * (Number(a.maxHeight) || 999);
        const bVolume = (Number(b.maxLength) || 999) * (Number(b.maxWidth) || 999) * (Number(b.maxHeight) || 999);
        return aVolume - bVolume;
      });

    return fittingMetrics[0] || null;
  };

  // Calculate material cost based on template dimensions and packing type
  const calculateMaterialCost = (
    length: number,
    width: number,
    height: number,
    packingType: PackingTypes | null,
    quantity: number
  ): number => {
    if (!packingType || !packingMetrics || !length || !width || !height) {
      return 0;
    }

    const metric = packingMetrics.find((m) => m.packingType === packingType);
    if (!metric) return 0;

    // Use materialRatePerSqFt if available, otherwise fall back to baseRate
    const materialRate = Number((metric as any).materialRatePerSqFt) || 0;
    const baseRate = Number(metric.baseRate) || 0;
    const ratePerSqFt = materialRate || baseRate;

    // Calculate surface area with 5% buffer
    const bufferedL = length * 1.05;
    const bufferedW = width * 1.05;
    const bufferedH = height * 1.05;
    const surfaceAreaSqFt =
      (2 * (bufferedL * bufferedW + bufferedL * bufferedH + bufferedW * bufferedH)) / 144;

    // Material cost per item
    const materialCostPerItem = surfaceAreaSqFt * ratePerSqFt;
    return materialCostPerItem * quantity;
  };


  // Calculate price based on selected templates and service type
  const handleCalculatePrice = () => {
    if (selectedTemplates.length === 0) {
      error({ message: "Please add at least one item template" });
      return;
    }

    // Calculate packing materials cost and duration
    let totalPackingMaterials = 0;
    let totalPackingDuration = 0; // in minutes

    selectedTemplates.forEach((item) => {
      if (item.wePack && item.templateId && item.packingType) {
        const template = inventoryTemplates?.find((t: any) => t.id === item.templateId);
        if (template) {
          // Calculate material cost
          const materialCost = calculateMaterialCost(
            template.length || 0,
            template.width || 0,
            template.height || 0,
            item.packingType,
            item.quantity
          );
          totalPackingMaterials += materialCost;

          // Calculate packing duration (minutes)
          const bestMetric = getBestMetricForType(item.templateId, item.packingType);
          if (bestMetric) {
            const estimatedMinutesPerItem = Number(bestMetric.estimatedMinutesPerItem) || 0;
            totalPackingDuration += estimatedMinutesPerItem * item.quantity;
          }
        }
      }
    });

    // Calculate service cost using billing tab logic
    const staffHourlyRate = artHandlerRole?.hourlyRate || 100;
    const totalHours = travelTime + duration;
    let serviceCost = 0;
    let staffCost = 0;
    let vesselCost = 0;

    if (
      selectedServiceType === ServiceType.COLLECTION ||
      selectedServiceType === ServiceType.DELIVERY
    ) {
      // Collection/Delivery: (Staff + Vehicle) × Total Hours
      staffCost = staffCount * staffHourlyRate * totalHours;
      
      // Vehicle cost (only if vessel is selected)
      if (selectedVesselId) {
        const vessel = vessels?.find((v: any) => v.id === selectedVesselId);
        const vesselHourlyRate = vessel?.hourlyRate || 90;
        vesselCost = vesselHourlyRate * totalHours;
      }
      
      serviceCost = staffCost + vesselCost;
    } else if (selectedServiceType === ServiceType.ON_SITE_SERVICE) {
      // On-Site Service: Staff Only × Site Time with $300 minimum per staff
      staffCost = staffCount * staffHourlyRate * duration; // No travel time for on-site
      const minimumCharge = 300 * staffCount;
      serviceCost = Math.max(staffCost, minimumCharge);
    } else if (selectedServiceType === ServiceType.WAREHOUSE_SERVICE) {
      // Warehouse: Volume-based pricing ($1/ft³ with $75 minimum)
      const totalCubicFeet = selectedTemplates.reduce((sum, item) => {
        const template = inventoryTemplates?.find((t: any) => t.id === item.templateId);
        if (template) {
          const cubicFeet = ((template.length || 0) * (template.width || 0) * (template.height || 0)) / 1728;
          return sum + (cubicFeet * item.quantity);
        }
        return sum;
      }, 0) || 60; // Default 60 cubic feet

      const warehouseRate = 1; // $1 per cubic foot
      const minimumCharge = 75; // $75 minimum
      serviceCost = Math.max(totalCubicFeet * warehouseRate, minimumCharge);
    } else if (selectedServiceType === ServiceType.FABRICATION_SERVICE) {
      // Fabrication: 1.5x hourly rate for specialized work
      staffCost = staffCount * (staffHourlyRate * 1.5) * duration; // Just site time, no travel
      serviceCost = staffCost;
    } else {
      // Fallback
      staffCost = staffCount * staffHourlyRate * totalHours;
      serviceCost = staffCost;
    }

    // Supplies cost (from packing materials)
    const suppliesCost = totalPackingMaterials;

    // Total cost (packing labor is included in duration, not charged separately)
    const total = serviceCost + suppliesCost;

    setCalculatedPrice(total);
    setPriceBreakdown({
      packingMaterials: totalPackingMaterials,
      packingDuration: totalPackingDuration,
      serviceCost,
      suppliesCost,
      total,
    });
    success({ message: `Estimated price: $${total.toFixed(2)}` });
  };

  // Handle save for quick estimate - create minimal booking, then navigate to detail page
  const handleSaveQuickEstimate = async () => {
    try {
      const values = await form.validateFields();
      const formValues = values as any;

      // Validate client info
      if (!selectedClientId && !showNewClientForm) {
        error({ message: "Please select a client or use Quick Client" });
        return;
      }

      if (showNewClientForm && !formValues.clientName) {
        error({ message: "Please enter client name" });
        return;
      }

      if (showNewClientForm && !formValues.clientEmail && !formValues.clientPhone) {
        error({ message: "Please provide either email or phone number" });
        return;
      }

      // Build minimal booking payload - just client info and sidemark
      const payload: any = {
        status: JobStatus.ESTIMATED, // Mark as estimated so estimate form shows
        bookingDate: dayjs().toISOString(),
      };

      // Add client info - either existing client or temp client data
      if (showNewClientForm && formValues.clientName) {
        // Always provide tempClientData as an object when using quick client
        payload.tempClientData = {
          name: formValues.clientName,
          email: formValues.clientEmail || '',
          phone: formValues.clientPhone || '',
        };
      } else if (selectedClientId) {
        payload.clientId = selectedClientId;
      }

      // Add sidemark if provided
      if (formValues.sidemark) {
        payload.sidemark = formValues.sidemark;
      }

      // Create minimal booking
      mutation.mutate(payload, {
        onSuccess: (booking: TBooking) => {
          // Store quick estimate data in sessionStorage to carry over to estimate form
          if (selectedTemplates.length > 0 || estimateNotes) {
            // Build additional info that can't be carried over
            const additionalInfo = [
              selectedVesselId && vessels?.find((v: any) => v.id === selectedVesselId) 
                ? `Vehicle: ${vessels?.find((v: any) => v.id === selectedVesselId)?.name || ''}` 
                : null,
              staffCount ? `Staff: ${staffCount}` : null,
              travelTime > 0 ? `Travel Time: ${travelTime} hrs` : null,
            ].filter(Boolean).join(", ");

            // Combine user notes with additional info
            const combinedNotes = [
              estimateNotes,
              additionalInfo ? `Quick Estimate Config: ${additionalInfo}` : null,
            ].filter(Boolean).join("\n\n");

            const quickEstimateData = {
              templates: selectedTemplates,
              serviceType: selectedServiceType,
              vesselId: selectedVesselId,
              travelTime,
              staffCount,
              duration,
              notes: combinedNotes,
            };
            sessionStorage.setItem(`quickEstimate_${booking.id}`, JSON.stringify(quickEstimateData));
          }
          
          success({ message: "Quick estimate booking created! Add services below." });
          closeBookingModal();
          // Navigate to booking detail page where estimate form is available
          navigate(`/bookings/${booking.id}`);
          qc.invalidateQueries({
            queryKey: BOOKING_KEYS.GET_BOOKINGS(filters),
          });
        },
        onError: (err: any) => {
          error({ message: err.message || "Failed to create quick estimate booking" });
        },
      });
    } catch (err) {
      console.error("Validation failed:", err);
    }
  };

  return (
    <Spin spinning={mutation.isPending || mutationUpdate.isPending || createEstimateMutation.isPending}>
      <Form
        form={form}
        layout="vertical"
        size="large"
        requiredMark={CustomizeRequiredMark}
        disabled={viewOnly}
        onFieldsChange={(_, allFields) => {
          const fields = allFields.reduce((acc, field) => {
            if (field.name[0] && field.value)
              acc[field.name[0] as keyof FieldType] = field.value;
            return acc;
          }, {} as FieldType);
          setInfoForm(fields);
        }}
      >
        {/* Estimate Mode Banner */}
        {isEstimateMode && (
          <Alert
            message={
              <div className="flex items-center space-x-2">
                <Zap size={18} className="text-green-600" />
                <span className="font-semibold">QUICK ESTIMATE MODE</span>
              </div>
            }
            description="Select an existing client or click 'Add New' to create a quick estimate with temporary contact info."
            type="success"
            showIcon={false}
            className="mb-6"
          />
        )}

        {/* Header with Edit Client Button */}
        {!isEstimateMode && selectedClientId && (
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Booking Details</h3>
            <Button
              htmlType="button"
              icon={<Edit size={16} />}
              onClick={handleEditClient}
              className="flex items-center space-x-2"
            >
              Edit Client
            </Button>
          </div>
        )}

        {/* Client Selection (shown in both modes) */}
        <Form.Item
          label="Client"
          name="clientId"
          rules={showNewClientForm ? [] : [{ required: true, message: "Please select a client" }]}
        >
          <div className="flex space-x-2">
            <Select
              className="flex-1"
              showSearch
              placeholder="Search clients..."
              optionFilterProp="children"
              loading={clientsLoading}
              value={selectedClientId}
              disabled={showNewClientForm}
              onChange={(value) => {
                form.setFieldValue('clientId', value);
                setPreservedClientId(value);
                // Clear temp client fields if selecting existing client
                if (isEstimateMode) {
                  (form as any).setFieldsValue({ clientName: undefined, clientEmail: undefined, clientPhone: undefined });
                  setShowNewClientForm(false);
                }
              }}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={clients?.items?.map((client) => ({
                value: client.id,
                label:
                  client.companyName ||
                  [client.firstName, client.lastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim() ||
                  "Unnamed Client",
              }))}
              size="large"
            />
            <Button
              htmlType="button"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => openClientModal()}
              title="Create full client"
            >
              New Client
            </Button>
            <Button
              htmlType="button"
              icon={<Zap size={16} />}
              size="large"
              type={showNewClientForm ? "default" : "dashed"}
              onClick={() => {
                setShowNewClientForm(!showNewClientForm);
                if (!showNewClientForm) {
                  // Clear client selection when showing new client form
                  form.setFieldValue('clientId', undefined);
                } else {
                  // Clear temp client fields when hiding form
                  (form as any).setFieldsValue({ clientName: undefined, clientEmail: undefined, clientPhone: undefined });
                }
              }}
              title="Quick contact with just phone or email"
            >
              {showNewClientForm ? "Cancel" : "Quick Client"}
            </Button>
          </div>
        </Form.Item>

        {/* Quick Client Form */}
        {showNewClientForm && (
          <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4 space-y-3">
            <div className="text-sm font-medium text-blue-800 mb-2">Quick Contact Info</div>

            <Form.Item
              label="Client Name"
              name="clientName"
              rules={[{ required: true, message: "Please enter client name" }]}
              className="mb-3"
            >
              <Input placeholder="e.g. John Smith" />
            </Form.Item>

            {/* Show matching existing clients */}
            {clientNameValue && clientNameValue.length >= 2 && (() => {
              const searchTerm = clientNameValue.toLowerCase();
              const matches = clients?.items?.filter((client) => {
                const name = (client.companyName || `${client.firstName} ${client.lastName}`).toLowerCase();
                const email = (client.email || '').toLowerCase();
                const phone = (client.contactPhone || '').toLowerCase();
                return name.includes(searchTerm) || email.includes(searchTerm) || phone.includes(searchTerm);
              }).slice(0, 3);

              if (!matches?.length) return null;

              return (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                  <div className="text-xs font-medium text-yellow-800 mb-2">
                    Possible existing clients found:
                  </div>
                  <div className="space-y-2">
                    {matches.map((client) => (
                      <div
                        key={client.id}
                        className="flex items-center justify-between bg-white p-2 rounded border cursor-pointer hover:bg-yellow-100"
                        onClick={() => {
                          form.setFieldValue('clientId', client.id);
                          setShowNewClientForm(false);
                          (form as any).setFieldsValue({ clientName: undefined, clientEmail: undefined, clientPhone: undefined });
                        }}
                      >
                        <div>
                          <div className="font-medium text-sm">
                            {client.companyName || `${client.firstName} ${client.lastName}`.trim()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {client.email} {client.contactPhone && `• ${client.contactPhone}`}
                          </div>
                        </div>
                        <Button type="link" size="small">Select</Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <Form.Item
              label="Email"
              name="clientEmail"
              rules={[
                { type: "email", message: "Please enter a valid email" },
              ]}
              className="mb-3"
            >
              <Input type="email" placeholder="client@example.com" />
            </Form.Item>

            <Form.Item
              label="Phone"
              name="clientPhone"
              className="mb-0"
            >
              <Input placeholder="(555) 123-4567" />
            </Form.Item>

            <div className="text-xs text-gray-600 mt-2">
              * Provide at least email or phone number
            </div>
          </div>
        )}

        {/* Sidemark */}
        <Form.Item
          label="Sidemark"
          name="sidemark"
          extra={<span className="text-xs text-gray-500">Optional 6-10 character shipment identifier</span>}
        >
          <Input
            placeholder="e.g. ABC123"
            maxLength={20}
            size="large"
          />
        </Form.Item>

        {/* Estimate Features - Only show in estimate mode */}
        {isEstimateMode && (
          <>
            {/* Service Configuration */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Service Type Selection */}
                <Form.Item label="Service Type">
                  <Select
                    value={selectedServiceType}
                    onChange={(value) => {
                      setSelectedServiceType(value);
                      // Reset defaults based on service type
                      if (value === ServiceType.ON_SITE_SERVICE) {
                        setStaffCount(1);
                        setTravelTime(0);
                        setSelectedVesselId(undefined);
                      } else if (value === ServiceType.COLLECTION || value === ServiceType.DELIVERY) {
                        setStaffCount(2);
                        setTravelTime(0.5);
                      } else {
                        setStaffCount(1);
                        setTravelTime(0);
                        setSelectedVesselId(undefined);
                      }
                      setCalculatedPrice(null);
                      setPriceBreakdown(null);
                    }}
                    size="large"
                    options={[
                      { label: "Collection", value: ServiceType.COLLECTION },
                      { label: "Delivery", value: ServiceType.DELIVERY },
                      { label: "Site Service", value: ServiceType.ON_SITE_SERVICE },
                      { label: "Warehouse", value: ServiceType.WAREHOUSE_SERVICE },
                      { label: "Fabrication", value: ServiceType.FABRICATION_SERVICE },
                    ]}
                  />
                </Form.Item>

                {/* Vessel Selection */}
                {(selectedServiceType === ServiceType.COLLECTION || selectedServiceType === ServiceType.DELIVERY) && (
                  <Form.Item label="Vehicle">
                    <Select
                      value={selectedVesselId}
                      onChange={(value) => {
                        setSelectedVesselId(value);
                        setCalculatedPrice(null);
                        setPriceBreakdown(null);
                      }}
                      size="large"
                      allowClear
                      placeholder="Select vehicle (optional)"
                      options={vessels?.map((v: any) => ({
                        label: v.name,
                        value: v.id,
                      }))}
                    />
                  </Form.Item>
                )}

                {/* Travel Time */}
                {(selectedServiceType === ServiceType.COLLECTION || selectedServiceType === ServiceType.DELIVERY) && (
                  <Form.Item label="Travel Time (hours)">
                    <InputNumber
                      value={travelTime}
                      onChange={(value) => {
                        setTravelTime(value || 0.5);
                        setCalculatedPrice(null);
                        setPriceBreakdown(null);
                      }}
                      min={0}
                      step={0.25}
                      size="large"
                      className="w-full"
                      addonAfter="hrs"
                    />
                  </Form.Item>
                )}

                {/* Duration */}
                <Form.Item label="Duration (hours)">
                  <InputNumber
                    value={duration}
                    onChange={(value) => {
                      setDuration(value || 1);
                      setCalculatedPrice(null);
                      setPriceBreakdown(null);
                    }}
                    min={0.25}
                    step={0.25}
                    size="large"
                    className="w-full"
                    addonAfter="hrs"
                  />
                </Form.Item>

                {/* Staff Count */}
                <Form.Item label="Staff Quantity">
                  <InputNumber
                    value={staffCount}
                    onChange={(value) => {
                      setStaffCount(value || 1);
                      setCalculatedPrice(null);
                      setPriceBreakdown(null);
                    }}
                    min={1}
                    step={1}
                    size="large"
                    className="w-full"
                  />
                </Form.Item>
              </div>
            </div>

            {/* Item Templates Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Package size={16} className="text-yellow-600" />
                  Item Templates
                </label>
                <Button
                  type="dashed"
                  size="small"
                  icon={<Plus size={14} />}
                  onClick={addTemplateRow}
                >
                  Add Item
                </Button>
              </div>

              {selectedTemplates.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedTemplates.map((item, index) => {
                    const availableTypes = item.templateId ? getAvailablePackingTypes(item.templateId) : [];

                    return (
                      <div key={index} className={`p-3 rounded border ${item.wePack ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Pack Toggle */}
                          <Tooltip title={item.wePack ? "AHC will pack this item" : "Pre-packed by shipper"}>
                            <Button
                              size="small"
                              type={item.wePack ? "primary" : "default"}
                              onClick={() => updateTemplateRow(index, 'wePack', !item.wePack)}
                            >
                              {item.wePack ? 'AHC PACK' : 'SHIPPER'}
                            </Button>
                          </Tooltip>

                          {/* Template Select */}
                          <Select
                            placeholder="Select template"
                            value={item.templateId || undefined}
                            onChange={(val) => updateTemplateRow(index, 'templateId', val)}
                            className="flex-1 min-w-[200px]"
                            size="small"
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={inventoryTemplates?.map((t: any) => ({
                              label: `${t.name} (${t.length}"×${t.width}"×${t.height}")`,
                              value: t.id,
                            }))}
                          />

                          {/* Quantity */}
                          <InputNumber
                            min={1}
                            value={item.quantity}
                            onChange={(val) => updateTemplateRow(index, 'quantity', val || 1)}
                            className="w-20"
                            size="small"
                            placeholder="Qty"
                          />

                          {/* Packing Type */}
                          {item.wePack ? (
                            <Select
                              placeholder="Packing type"
                              value={item.packingType}
                              onChange={(val) => updateTemplateRow(index, 'packingType', val)}
                              className="w-36"
                              size="small"
                              disabled={!item.templateId}
                              options={availableTypes.map((type) => ({
                                label: type.replace(/_/g, ' '),
                                value: type,
                              }))}
                              notFoundContent={<span className="text-xs text-gray-400">No types fit</span>}
                            />
                          ) : (
                            <Select
                              placeholder="Packing type"
                              value={item.packingType}
                              onChange={(val) => updateTemplateRow(index, 'packingType', val)}
                              className="w-36"
                              size="small"
                              allowClear
                              options={Object.values(PackingTypes).map((type) => ({
                                label: type.replace(/_/g, ' '),
                                value: type,
                              }))}
                            />
                          )}

                          {/* Remove Button */}
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<Trash2 size={14} />}
                            onClick={() => removeTemplateRow(index)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded">
                  Click "Add Item" to add item templates
                </div>
              )}
            </div>

            {/* Notes/Scope Section */}
            <Form.Item label="Notes / Scope">
              <Input.TextArea
                value={estimateNotes}
                onChange={(e) => setEstimateNotes(e.target.value)}
                rows={3}
                placeholder="Enter any notes or scope details for this estimate..."
                size="large"
              />
            </Form.Item>

            {/* Calculate Price Section */}
            {calculatedPrice !== null && priceBreakdown && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-semibold">Price Breakdown</span>
                  <span className="text-3xl font-bold text-green-600">${calculatedPrice.toFixed(2)}</span>
                </div>
                <div className="space-y-2 text-sm">
                  {priceBreakdown.packingMaterials > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Packing Materials:</span>
                      <span className="font-medium">${priceBreakdown.packingMaterials.toFixed(2)}</span>
                    </div>
                  )}
                  {priceBreakdown.packingDuration > 0 && (
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-600">Estimated Packing Duration:</span>
                        <span className="text-xs text-gray-500 ml-1">(per tech hour)</span>
                      </div>
                      <span className="font-medium">
                        {Math.round(priceBreakdown.packingDuration)} min
                        {priceBreakdown.packingDuration >= 60 && ` (${(priceBreakdown.packingDuration / 60).toFixed(1)} hrs)`}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Cost:</span>
                    <span className="font-medium">${priceBreakdown.serviceCost.toFixed(2)}</span>
                  </div>
                  {priceBreakdown.suppliesCost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Supplies:</span>
                      <span className="font-medium">${priceBreakdown.suppliesCost.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-green-300 pt-2 mt-2 flex justify-between">
                    <span className="font-semibold">Total:</span>
                    <span className="font-bold text-green-700">${priceBreakdown.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Project Selection - Only show in normal mode */}
        {!isEstimateMode && (
          <Form.Item label="Project" name="projectId">
          <InfiniteScrollSelect
            getOptionLabel={(item) => item.name}
            getOptionValue={(item) => item.id}
            queryResult={infiniteProjects}
            showSearch={true}
            size="large"
            placeholder="Select or create new project"
            onChange={(value) => {
              console.log('Project selected:', value);
              form.setFieldValue('projectId', value);
            }}
          />
        </Form.Item>
        )}

        {!isEstimateMode && (
          <Button
            htmlType="button"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setOpenCreateProjectModal(true)}
            className="mt-2"
          >
            Create New Project
          </Button>
        )}

        {/* Action Buttons */}
        {!viewOnly && (
          <div className="flex gap-3 justify-end">
            {isEstimateMode ? (
              <>
                <Button
                  htmlType="button"
                  icon={<Calculator size={16} />}
                  onClick={handleCalculatePrice}
                  disabled={selectedTemplates.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                >
                  Calculate Price
                </Button>
                <Button
                  htmlType="button"
                  icon={<Save size={16} />}
                  onClick={handleSaveQuickEstimate}
                  loading={mutation.isPending}
                  disabled={!selectedClientId && !showNewClientForm}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  Create Booking & Add Services
                </Button>
              </>
            ) : (
              <>
                <Button
                  htmlType="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center space-x-2"
                >
                  <Save size={16} />
                  <span>{isEditMode ? "Save" : "Create"}</span>
                </Button>
                {isEditMode && (
                  <Button
                    htmlType="button"
                    onClick={handleAddInventory}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <Package size={16} />
                    <span>Add Inventory</span>
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </Form>

      <FormProject
        open={openCreateProject}
        setOpen={setOpenCreateProjectModal}
      />
    </Spin>
  );
};